/**
 * VCloud Service Groups Builder
 * Groups main service and addon services with their config options
 */
import type { TFunction } from 'i18next'

// ============================================================================
// Types
// ============================================================================

export interface ServiceGroup {
    serviceId: string
    serviceName: string
    serviceStatus: string
    isMain: boolean
    items: any[]
    employee_names?: string[]
    service_start_date?: string
    current_startdate?: string
    current_enddate?: string
    continue_startdate?: string
    continue_enddate?: string
    service_tax_percentage?: number
    service_price_register?: number
    service_pricing_model?: string
}

// ============================================================================
// Builder
// ============================================================================

interface BuildServiceGroupsOptions {
    service: any
    data: any
    t: TFunction
}

export const buildServiceGroups = ({
    service,
    data,
    t,
}: BuildServiceGroupsOptions): Map<string, ServiceGroup> => {
    const serviceGroups: Map<string, ServiceGroup> = new Map()

    // Main service group
    const mainServiceId = service?.service_id || 'main'
    serviceGroups.set(mainServiceId, {
        serviceId: mainServiceId,
        serviceName:
            service?.service_name ||
            t('main_service', { defaultValue: 'Dịch vụ chính' }),
        serviceStatus: service?.service_status,
        isMain: true,
        items: [],
        employee_names: service?.employee_names,
        service_start_date: service?.service_start_date,
        current_startdate: service?.current_startdate,
        current_enddate: service?.current_enddate,
        continue_startdate: service?.continue_startdate,
        continue_enddate: service?.continue_enddate,
        service_tax_percentage: service?.service_tax_percentage,
        service_price_register: service?.service_price_register,
        service_pricing_model: service?.service_pricing_model,
    })

    // Add main service config options
    const mainConfigs = (data.configoptions || []).filter(
        (item: any) =>
            item.commitment_value !== null &&
            item.commitment_value !== undefined &&
            item.commitment_value > 0
    )
    serviceGroups.get(mainServiceId)!.items = mainConfigs.map((item: any) => ({
        ...item,
        isAddon: false,
    }))

    // Addon service groups
    if (data.addons && data.addons.length > 0) {
        data.addons.forEach((addon: any) => {
            const addonConfigs = (addon.configoptions || []).filter(
                (item: any) =>
                    item.commitment_value !== null &&
                    item.commitment_value !== undefined &&
                    item.commitment_value > 0
            )
            if (addonConfigs.length > 0) {
                serviceGroups.set(addon.service_id, {
                    serviceId: addon.service_id,
                    serviceName:
                        addon.service_name ||
                        t('addon_service', { defaultValue: 'Addon' }),
                    serviceStatus: addon.service_status,
                    isMain: false,
                    items: addonConfigs.map((item: any) => ({
                        ...item,
                        isAddon: true,
                        addon_service_id: addon.service_id,
                        addon_service_name: addon.service_name,
                        addon_service_status: addon.service_status,
                    })),
                    employee_names: service?.employee_names,
                    service_start_date:
                        addon.service_start_date || service?.service_start_date,
                    current_startdate:
                        addon.current_startdate || service?.current_startdate,
                    current_enddate:
                        addon.current_enddate || service?.current_enddate,
                    continue_startdate:
                        addon.continue_startdate || service?.continue_startdate,
                    continue_enddate:
                        addon.continue_enddate || service?.continue_enddate,
                    service_tax_percentage: service?.service_tax_percentage,
                    service_price_register: addon.service_price_register,
                    service_pricing_model: addon.service_pricing_model,
                })
            }
        })
    }

    return serviceGroups
}

// ============================================================================
// Utilities
// ============================================================================

export const getTotalItems = (
    serviceGroups: Map<string, ServiceGroup>
): number => {
    return Array.from(serviceGroups.values()).reduce(
        (sum, g) => sum + g.items.length,
        0
    )
}

export const getSortedGroups = (
    serviceGroups: Map<string, ServiceGroup>
): ServiceGroup[] => {
    return Array.from(serviceGroups.values()).sort((a, _b) =>
        a.isMain ? -1 : 1
    )
}
